import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FiArrowLeft, FiHome } from 'react-icons/fi';

export default function UnitEdit({ unit, facilities = [], room_types = [], user }) {
  const { data, setData, patch, processing, errors } = useForm({
    facility_id: unit.facility_id ? unit.facility_id.toString() : '',
    room_type: unit.room_type || '',
    bedrooms: unit.bedrooms || 1,
    bathrooms: unit.bathrooms || 1,
    monthly_cost_cents: unit.monthly_cost_cents || 0,
    available_count: unit.available_count || 0,
    square_feet: unit.square_feet || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    patch(`/facility_owner/units/${unit.id}`);
  };

  // Format room type for display
  const formatRoomType = (roomType) => {
    return roomType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/units">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Units
              </Link>
            </Button>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Edit Unit</h1>
                <p className="text-gray-500">Update the details for this unit type</p>
              </div>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Unit Details</CardTitle>
              <CardDescription>Modify the details for this unit type</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="facility_id">Facility</Label>
                    <Select
                      value={data.facility_id}
                      onValueChange={(value) => setData('facility_id', value)}
                    >
                      <SelectTrigger className={errors.facility_id ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select a facility" />
                      </SelectTrigger>
                      <SelectContent>
                        {facilities.map((facility) => (
                          <SelectItem key={facility.id} value={facility.id.toString()}>
                            {facility.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.facility_id && <p className="text-red-500 text-sm mt-1">{errors.facility_id}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="room_type">Room Type</Label>
                    <Select
                      value={data.room_type}
                      onValueChange={(value) => setData('room_type', value)}
                    >
                      <SelectTrigger className={errors.room_type ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select room type" />
                      </SelectTrigger>
                      <SelectContent>
                        {room_types.map((type) => (
                          <SelectItem key={type} value={type}>
                            {formatRoomType(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.room_type && <p className="text-red-500 text-sm mt-1">{errors.room_type}</p>}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="bedrooms">Bedrooms</Label>
                      <Input
                        id="bedrooms"
                        type="number"
                        min="0"
                        value={data.bedrooms}
                        onChange={e => setData('bedrooms', e.target.value)}
                        className={errors.bedrooms ? 'border-red-500' : ''}
                      />
                      {errors.bedrooms && <p className="text-red-500 text-sm mt-1">{errors.bedrooms}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="bathrooms">Bathrooms</Label>
                      <Input
                        id="bathrooms"
                        type="number"
                        min="0"
                        step="0.5"
                        value={data.bathrooms}
                        onChange={e => setData('bathrooms', e.target.value)}
                        className={errors.bathrooms ? 'border-red-500' : ''}
                      />
                      {errors.bathrooms && <p className="text-red-500 text-sm mt-1">{errors.bathrooms}</p>}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="monthly_cost_cents">Monthly Cost ($)</Label>
                    <Input
                      id="monthly_cost_cents"
                      type="number"
                      min="0"
                      value={data.monthly_cost_cents ? (data.monthly_cost_cents / 100) : 0}
                      onChange={e => setData('monthly_cost_cents', Math.round(parseFloat(e.target.value || 0) * 100))}
                      className={errors.monthly_cost_cents ? 'border-red-500' : ''}
                    />
                    {errors.monthly_cost_cents && <p className="text-red-500 text-sm mt-1">{errors.monthly_cost_cents}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="available_count">Available Units</Label>
                    <Input
                      id="available_count"
                      type="number"
                      min="0"
                      value={data.available_count}
                      onChange={e => setData('available_count', e.target.value)}
                      className={errors.available_count ? 'border-red-500' : ''}
                    />
                    {errors.available_count && <p className="text-red-500 text-sm mt-1">{errors.available_count}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="square_feet">Square Footage</Label>
                    <Input
                      id="square_feet"
                      type="number"
                      min="0"
                      value={data.square_feet}
                      onChange={e => setData('square_feet', e.target.value)}
                      className={errors.square_feet ? 'border-red-500' : ''}
                    />
                    {errors.square_feet && <p className="text-red-500 text-sm mt-1">{errors.square_feet}</p>}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" asChild>
                    <Link href="/facility_owner/units">Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    Update Unit
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
